/**
 * Mega menu v2 (desktop)
 * - Hover opens the panel; clicking the top link goes to its category page.
 * - Keyboard: Enter/Space on the top link toggles the panel, Escape closes it.
 * - Hovering (or focusing) a submenu link shows its product grid, after a short
 *   delay so a diagonal move toward the products doesn't switch grids on the way.
 */
(() => {
  if (customElements.get('mega-v2-details')) return;

  const OPEN_DELAY = 100;
  const CLOSE_DELAY = 200;
  const SWITCH_DELAY = 120;

  let isScrolling = false;
  let scrollTimer;
  window.addEventListener('scroll', () => {
    isScrolling = true;
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => { isScrolling = false; }, 100);
  }, { passive: true });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details.mega-v2-details[open]').forEach((details) => {
      const hadFocus = details.contains(document.activeElement);
      details.closePanel();
      if (hadFocus) details.summary.focus();
    });
  });

  class MegaV2Details extends HTMLDetailsElement {
    connectedCallback() {
      if (this.summary) return;
      this.summary = this.querySelector('summary');
      this.panel = this.querySelector('[data-mega-v2-panel]');
      this.header = this.closest('.header');

      this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));
      this.panel?.addEventListener('mouseover', this.onPanelMouseOver.bind(this));
      this.panel?.addEventListener('focusin', this.onPanelFocusIn.bind(this));

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', () => this.openPanel());
        this.addEventListener('shopify:block:deselect', () => this.closePanel());
      }
    }

    onMouseEnter() {
      clearTimeout(this.closeTimer);
      if (isScrolling || this.open) return;
      this.loadImages(this.activeGrid);
      clearTimeout(this.openTimer);
      this.openTimer = setTimeout(() => this.openPanel(), OPEN_DELAY);
    }

    onMouseLeave() {
      clearTimeout(this.openTimer);
      clearTimeout(this.switchTimer);
      this.closeTimer = setTimeout(() => this.closePanel(), CLOSE_DELAY);
    }

    onSummaryClick(event) {
      event.preventDefault();
      // detail is 0 for keyboard-generated clicks (Enter/Space)
      if (event.detail === 0) {
        this.open ? this.closePanel() : this.openPanel();
        return;
      }
      if (this.summary.dataset.link) window.location.href = this.summary.dataset.link;
    }

    onPanelMouseOver(event) {
      const trigger = event.target.closest('[data-mega-v2-trigger]');
      if (!trigger) {
        clearTimeout(this.switchTimer);
        this.pendingIndex = null;
        return;
      }
      const index = trigger.dataset.megaV2Trigger;
      if (trigger.classList.contains('is-active') || index === this.pendingIndex) return;

      this.loadImages(this.gridAt(index));
      clearTimeout(this.switchTimer);
      this.pendingIndex = index;
      this.switchTimer = setTimeout(() => this.activate(index), SWITCH_DELAY);
    }

    onPanelFocusIn(event) {
      const trigger = event.target.closest('[data-mega-v2-trigger]');
      if (trigger) this.activate(trigger.dataset.megaV2Trigger);
    }

    openPanel() {
      clearTimeout(this.closeTimer);
      if (this.open) return;
      document.querySelectorAll('details.mega-v2-details[open]').forEach((details) => {
        if (details !== this) details.closePanel();
      });
      if (this.header) {
        this.style.setProperty('--mega-v2-top', `${Math.round(this.header.getBoundingClientRect().bottom)}px`);
      }
      this.activate('0');
      this.open = true;
    }

    closePanel() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      clearTimeout(this.switchTimer);
      this.pendingIndex = null;
      this.open = false;
    }

    activate(index) {
      clearTimeout(this.switchTimer);
      this.pendingIndex = null;
      this.panel?.querySelectorAll('[data-mega-v2-trigger]').forEach((trigger) => {
        trigger.classList.toggle('is-active', trigger.dataset.megaV2Trigger === index);
      });
      this.panel?.querySelectorAll('[data-mega-v2-grid]').forEach((grid) => {
        grid.classList.toggle('is-active', grid.dataset.megaV2Grid === index);
      });
      this.loadImages(this.gridAt(index));
    }

    gridAt(index) {
      return this.panel?.querySelector(`[data-mega-v2-grid="${index}"]`);
    }

    get activeGrid() {
      return this.panel?.querySelector('[data-mega-v2-grid].is-active');
    }

    // Grids are lazy images inside a closed <details>; start the downloads just before they show.
    loadImages(grid) {
      grid?.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    }
  }

  customElements.define('mega-v2-details', MegaV2Details, { extends: 'details' });

  // Black bar on the header's grey line, under the hovered menu link (any link, with or without a panel),
  // otherwise under the link whose panel is open. Switching links cross-fades it:
  // the old bar fades out while a new one fades in under the new link.
  let indicatorTimer;
  let hoveredNavItem = null;
  const placeIndicator = (indicator, text) => {
    const origin = (indicator.offsetParent || indicator.parentElement).getBoundingClientRect();
    const box = text.getBoundingClientRect();
    indicator.style.width = `${box.width}px`;
    indicator.style.transform = `translateX(${box.left - origin.left}px)`;
  };
  const updateIndicator = () => {
    clearTimeout(indicatorTimer);
    // Short wait: switching menus closes one panel just before opening the next
    indicatorTimer = setTimeout(() => {
      document.querySelectorAll('.header').forEach((header) => {
        const nav = header.querySelector('.mega-v2-nav');
        if (!nav) return;
        const hovered = nav.contains(hoveredNavItem) ? hoveredNavItem.querySelector('.mega-v2-nav__text') : null;
        const text = hovered || nav.querySelector('details.mega-v2-details[open] .mega-v2-nav__text');
        const current = header.querySelector('.mega-v2-indicator.is-visible');

        if (current && current.megaV2Text === text) {
          placeIndicator(current, text);
          return;
        }
        if (current) {
          current.classList.remove('is-visible');
          setTimeout(() => current.remove(), 200);
        }
        if (!text) return;

        const indicator = document.createElement('span');
        indicator.className = 'mega-v2-indicator';
        indicator.setAttribute('aria-hidden', 'true');
        indicator.megaV2Text = text;
        header.append(indicator);
        placeIndicator(indicator, text);
        indicator.offsetWidth; // start from opacity 0
        indicator.classList.add('is-visible');
      });
    }, 40);
  };

  // `toggle` doesn't bubble: listen in the capture phase
  document.addEventListener('toggle', (event) => {
    if (event.target.matches?.('details.mega-v2-details')) updateIndicator();
  }, true);
  window.addEventListener('resize', updateIndicator);

  // Hovered top link (only the link itself: inside an open panel the bar stays on that panel's link)
  document.addEventListener('mouseover', (event) => {
    const item = event.target.closest?.('.mega-v2-nav__trigger, .mega-v2-nav__link')?.closest('.mega-v2-nav__item') || null;
    if (item === hoveredNavItem) return;
    hoveredNavItem = item;
    updateIndicator();
  });

  // Menu V3 card swatches: show that variant's photos in the card and point the card link to that variant.
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-mega-v3-swatch]');
    const card = swatch?.closest('.mega-v3-card');
    const link = card?.querySelector('.mega-v2-card__link');
    const media = card?.querySelector('.mega-v2-card__media');
    if (!link || !media) return;
    event.preventDefault();

    // The card's own photos, for the default swatch
    if (!card.megaV3Default) {
      const primary = media.querySelector('.mega-v2-card__image--primary');
      const hover = media.querySelector('.mega-v2-card__image--hover');
      card.megaV3Default = {
        src: primary?.getAttribute('src'),
        srcset: primary?.getAttribute('srcset'),
        hoverSrc: hover?.getAttribute('src'),
        hoverSrcset: hover?.getAttribute('srcset'),
      };
    }

    const photos = swatch.hasAttribute('data-default') ? card.megaV3Default : swatch.dataset;
    const primary = media.querySelector('.mega-v2-card__image--primary');
    if (primary && photos.src) {
      setImage(primary, photos.src, photos.srcset);

      let hover = media.querySelector('.mega-v2-card__image--hover');
      if (photos.hoverSrc) {
        if (!hover) {
          hover = primary.cloneNode();
          hover.classList.replace('mega-v2-card__image--primary', 'mega-v2-card__image--hover');
          media.append(hover);
        }
        setImage(hover, photos.hoverSrc, photos.hoverSrcset);
      } else {
        hover?.remove();
      }
      media.classList.toggle('mega-v2-card__media--hover', Boolean(photos.hoverSrc));
    }

    link.href = swatch.dataset.url;
    card.querySelectorAll('[data-mega-v3-swatch]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === swatch));
    });
  });

  function setImage(img, src, srcset) {
    img.loading = 'eager';
    if (srcset) img.srcset = srcset;
    else img.removeAttribute('srcset');
    img.src = src;
  }
})();
